// src/features/services/media/utils/logger.ts
type Level = 'debug' | 'info' | 'warn' | 'error';
type Sink = (level: Level, ns: string, tag?: string, payload?: unknown) => void;

const isDev =
  (typeof process !== 'undefined' && (process as any).env?.NODE_ENV === 'development') ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV);

/** --- Helpers --- */
function parseList(raw?: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').map(x => x.trim()).filter(Boolean);
}

function nsEnabled(ns: string, enabled: string[]): boolean {
  if (!enabled.length) return false;
  return enabled.some(rule => {
    if (rule === '*' || rule === ns) return true;
    if (rule.endsWith('*')) return ns.startsWith(rule.slice(0, -1));
    return false;
  });
}

/** --- Enable list (persisted in localStorage) --- */
function getEnabled(): string[] {
  try {
    const raw = localStorage.getItem('LOG');
    if (raw && raw.trim()) return parseList(raw);
    // Allow DIAG_MEDIA to enable logs even in production builds
    try {
      const w: any = (typeof window !== 'undefined') ? window : {};
      const diag = (w.DIAG_MEDIA === true) || (() => { try { const v = localStorage.getItem('DIAG_MEDIA'); return v === '1' || v === 'true'; } catch { return false; } })();
      if (diag) return ['media:*'];
    } catch {}
  } catch {}
  return isDev ? ['media:*'] : [];
}

let enabled = getEnabled();

function enable(patterns: string | string[]) {
  const arr = Array.isArray(patterns) ? patterns : parseList(patterns);
  enabled = arr;
  try { localStorage.setItem('LOG', arr.join(',')); } catch {}
}

/** --- Console sink --- */
const consoleSink: Sink = (level, ns, tag, payload) => {
  const ts = new Date().toISOString().replace('T', ' ').replace('Z', '');
  const hdr = `[${ns}] ${level.toUpperCase()}${tag ? ' ' + tag : ''}`;
  const line = `${ts} ${hdr}`;
  // eslint-disable-next-line no-console
  const fn = (console as any)[level] ?? console.log;
  try {
    fn.call(console, line, payload ?? '');
  } catch {
    console.log(line, payload ?? '');
  }
};

/** --- Ring buffer --- */
type Entry = { t: number; level: Level; ns: string; tag?: string; payload?: unknown };
const ring: Entry[] = [];
const RING_MAX = 500;

function pushRing(entry: Entry) {
  ring.push(entry);
  if (ring.length > RING_MAX) ring.splice(0, ring.length - RING_MAX);
  try { (window as any).__LOGS__ = ring; } catch {}
}

/** --- Core emit --- */
function emit(level: Level, ns: string, tag?: string, payload?: unknown) {
  const e: Entry = { t: Date.now(), level, ns, tag, payload };
  pushRing(e);
  consoleSink(level, ns, tag, payload);
}

function make(level: Level) {
  return (ns: string, tag?: string, payload?: unknown) => {
    if (nsEnabled(ns, enabled)) emit(level, ns, tag, payload);
  };
}

function throttled(ns: string, tagBase: string, ms = 1000) {
  let last = 0;
  return (level: Level, tag?: string, payload?: unknown) => {
    if (!nsEnabled(ns, enabled)) return;
    const now = Date.now();
    if (now - last >= ms) {
      last = now;
      emit(level, ns, tag ?? tagBase, payload);
    }
  };
}

/** --- Public API --- */
export const L = {
  debug: make('debug'),
  info: make('info'),
  warn: make('warn'),
  error: make('error'),
  throttled,
  enable,                     // e.g. L.enable('media:*')
  _dump: () => ring.slice(),  // copy of ring buffer
};

// Expose for console tinkering
try { (window as any).L = L; } catch {}

// Auto-enable in dev if nothing stored
if (isDev && !localStorage.getItem('LOG')) {
  enable('media:*');
}
